import PropTypes from 'prop-types';
import { useState } from 'react';
import { Icon } from '@iconify/react';
import { NavLink as RouterLink, matchPath, useLocation } from 'react-router-dom';
import arrowIosForwardFill from '@iconify/icons-eva/arrow-ios-forward-fill';
import arrowIosDownwardFill from '@iconify/icons-eva/arrow-ios-downward-fill';
// material
import { alpha, useTheme, styled } from '@mui/material/styles';
import {
  Box,
  List,
  Collapse,
  ListItemText,
  ListItemIcon,
  ListSubheader,
  ListItemButton,
  Typography,
} from '@mui/material';
import ChartsNavItem from './ChartsNavItem';

const ListSubheaderStyle = styled((props) => <ListSubheader disableSticky disableGutters {...props} />)(
  ({ theme }) => ({
    ...theme.typography.overline,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    paddingLeft: theme.spacing(5),
    color: theme.palette.text.primary,
  }),
);

const ListItemStyle = styled(ListItemButton)(({ theme }) => ({
  ...theme.typography.body2,
  minHeight: 48,
  position: 'relative',
  textTransform: 'capitalize',
  paddingLeft: theme.spacing(5),
  paddingRight: theme.spacing(2.5),
  borderRadius: 1.5,
  color: theme.palette.text.secondary,
}));

const ListItemIconStyle = styled(ListItemIcon)({
  minWidth: 20,
  width: 20,
  height: 20,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: 12,
});

NavItem.propTypes = {
  isShow: PropTypes.bool,
  item: PropTypes.object,
};

function NavItem({ item, isShow }) {
  const theme = useTheme();
  const { pathname } = useLocation();
  const { title, path, icon, info, children } = item;
  const isActiveRoot = path ? !!matchPath({ path, end: false }, pathname) : false;

  const [open, setOpen] = useState(isActiveRoot);

  const handleOpen = () => {
    setOpen(!open);
  };

  const activeRootStyle = {
    bgcolor: theme.palette.primary.main,
    color: 'common.white',
    '&:hover': {
      bgcolor: theme.palette.primary.main,
    },
  };

  const activeSubStyle = {
    bgcolor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
    color: 'primary.main',
    '&:hover': {
      bgcolor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
    },
  };

  if (children) {
    return (
      <>
        <ListItemStyle
          onClick={handleOpen}
          sx={{
            ...(isActiveRoot && activeRootStyle),
            ...(!isActiveRoot && {
              '&:hover': {
                bgcolor: alpha(theme.palette.action.hover, 0.5),
              },
            }),
          }}
        >
          <ListItemIconStyle>
            {icon && (
              <Box
                sx={{
                  color: isActiveRoot ? 'common.white' : 'text.secondary',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {icon}
              </Box>
            )}
          </ListItemIconStyle>

          {isShow && (
            <>
              <ListItemText
                disableTypography
                primary={
                  <Typography
                    variant='body2'
                    sx={{
                      fontSize: 13,
                      fontWeight: isActiveRoot ? 500 : 400,
                      color: isActiveRoot ? 'common.white' : 'text.primary',
                    }}
                  >
                    {title}
                  </Typography>
                }
              />
              {info && info}
              <Box
                component={Icon}
                icon={open ? arrowIosDownwardFill : arrowIosForwardFill}
                sx={{
                  width: 16,
                  height: 16,
                  ml: 1,
                  color: isActiveRoot ? theme.palette.common.white : theme.palette.text.secondary,
                }}
              />
            </>
          )}
        </ListItemStyle>

        {isShow && (
          <Collapse in={open} timeout='auto' unmountOnExit>
            <List component='div' disablePadding sx={{ pl: 5.5, pt: 0.5 }}>
              {children.map((item) => {
                const { title, path, icon: childIcon } = item;
                const isActiveSub = path ? !!matchPath({ path, end: false }, pathname) : false;

                return (
                  <ListItemStyle
                    key={title}
                    component={RouterLink}
                    to={path}
                    sx={{
                      px: 2,
                      py: 0.75,
                      minHeight: 36,
                      mb: 0.25,
                      ...(isActiveSub && activeSubStyle),
                      ...(!isActiveSub && {
                        '&:hover': {
                          bgcolor: alpha(theme.palette.action.hover, 0.5),
                        },
                      }),
                    }}
                  >
                    <ListItemIconStyle>
                      {childIcon ? (
                        <Box
                          sx={{
                            color: isActiveSub ? 'primary.main' : 'text.secondary',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {childIcon}
                        </Box>
                      ) : (
                        <Box
                          component='span'
                          sx={{
                            width: 4,
                            height: 4,
                            display: 'flex',
                            borderRadius: '50%',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'text.disabled',
                            transition: (theme) => theme.transitions.create('transform'),
                            ...(isActiveSub && {
                              transform: 'scale(2)',
                              bgcolor: 'primary.main',
                            }),
                          }}
                        />
                      )}
                    </ListItemIconStyle>
                    <ListItemText
                      disableTypography
                      primary={
                        <Typography
                          variant='body2'
                          sx={{
                            fontSize: 13,
                            fontWeight: isActiveSub ? 500 : 400,
                            color: isActiveSub ? 'primary.main' : 'text.secondary',
                          }}
                        >
                          {title}
                        </Typography>
                      }
                    />
                  </ListItemStyle>
                );
              })}
            </List>
          </Collapse>
        )}
      </>
    );
  }

  return (
    <ListItemStyle
      component={RouterLink}
      to={path}
      sx={{
        ...(isActiveRoot && activeRootStyle),
        ...(!isActiveRoot && {
          '&:hover': {
            bgcolor: alpha(theme.palette.action.hover, 0.5),
          },
        }),
      }}
    >
      <ListItemIconStyle>
        {icon && (
          <Box
            sx={{
              color: isActiveRoot ? 'common.white' : 'text.secondary',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
        )}
      </ListItemIconStyle>
      {isShow && (
        <>
          <ListItemText
            disableTypography
            primary={
              <Typography
                variant='body2'
                sx={{
                  fontSize: 13,
                  fontWeight: isActiveRoot ? 500 : 400,
                  color: isActiveRoot ? 'common.white' : 'text.primary',
                }}
              >
                {title}
              </Typography>
            }
          />
          {info && info}
        </>
      )}
    </ListItemStyle>
  );
}

NavSection.propTypes = {
  isShow: PropTypes.bool,
  navConfig: PropTypes.array,
};

export default function NavSection({ navConfig, isShow = true, ...other }) {
  return (
    <Box {...other}>
      {navConfig.map((list) => {
        const { subheader, items } = list;
        return (
          <List key={subheader} disablePadding>
            {isShow && <ListSubheaderStyle>{subheader}</ListSubheaderStyle>}
            {items.map((item) => {
              // Use ChartsNavItem for charts menu (it handles its own grouping internally)
              if (item.title === 'charts') {
                return <ChartsNavItem key={item.title} item={item} isShow={isShow} />;
              }
              return <NavItem key={item.title} item={item} isShow={isShow} />;
            })}
          </List>
        );
      })}
    </Box>
  );
}
